<template>
  <div class="formulario-modelo">
    <!-- Cabecera -->
    <div class="header-flex">
      <div>
        <h1>Comparativo de Evaluación</h1>
        <p class="subtitulo">
          Ejecución #{{ idEjecucion }}
          <template v-if="comparacion"> · Escala 0–{{ comparacion.escala_max }}</template>
          <template v-if="comparacion?.factor"> · {{ comparacion.factor.nombre }}</template>
        </p>
      </div>
      <button class="btn salir" @click="$router.push({ name: 'ProcesoAutoevaluacionUsuario' })">Volver</button>
    </div>

    <div class="divider"></div>

    <!-- Controles -->
    <div class="controles">
      <div class="control">
        <label>Periodo anterior</label>
        <select v-model="anteriorSel" class="input-select">
          <option :value="null" disabled>Selecciona un periodo</option>
          <option v-for="c in candidatosAnterior" :key="c.id" :value="c.id">
            {{ c.periodo }} — {{ c.estado }}
          </option>
        </select>
      </div>

      <div class="control">
        <label>Nivel</label>
        <select v-model="factorSel" class="input-select" :disabled="!factores.length">
          <option :value="null">Todos los factores (resumen)</option>
          <option v-for="f in factores" :key="f.id" :value="f.id">
            {{ f.ordinal ? fmtOrdinal(f.ordinal) + ' ' : '' }}{{ f.nombre }}
          </option>
        </select>
      </div>

      <!-- Solo tiene sentido si los dos periodos usan escalas distintas: con la
           misma escala no hay nada que convertir. -->
      <div v-if="comparacion?.escalas_distintas" class="control">
        <label>Escala de lectura</label>
        <select v-model="escalaUnificada" class="input-select">
          <option :value="null">Cada periodo con la suya</option>
          <option :value="5">Todo en 0–5</option>
          <option :value="10">Todo en 1–10</option>
        </select>
      </div>

      <div v-if="factorSel" class="control control-btn">
        <button class="btn salir btn-sm" @click="factorSel = null">‹ Todos los factores</button>
      </div>
    </div>

    <p v-if="comparacion?.escalas_distintas && escalaUnificada !== null" class="ayuda-escala">
      Notas convertidas para poder leerlas juntas
      ({{ comparacion.actual.periodo }} va en 0–{{ comparacion.escala_max }} y
      {{ comparacion.anterior.periodo }} en 0–{{ comparacion.escala_max_anterior }}).
      El <strong>grado de cumplimiento no cambia</strong>: lo sigue dando la rúbrica
      del modelo de cada periodo, que es la que firmó ese informe.
    </p>

    <!-- Estados -->
    <div v-if="!anteriorSel" class="aviso">
      No hay un periodo anterior de la misma área para comparar. Registra o enlaza otra ejecución.
    </div>
    <div v-else-if="comparStore.cargando" class="info">Cargando comparación…</div>
    <div v-else-if="comparStore.error" class="aviso aviso-error">{{ comparStore.error }}</div>

    <template v-else-if="comparacion">
      <!-- Barra: periodos + pestañas vista + toggle emparejar -->
      <div class="barra-superior">
        <div class="periodos-bar">
          <span class="badge badge-actual">Actual: {{ comparacion.actual.periodo }}</span>
          <span class="badge badge-anterior">Anterior: {{ comparacion.anterior.periodo }}</span>
        </div>
        <div class="acciones-derecha">
          <div class="vista-tabs">
            <button class="vtab" :class="{ activo: vista === 'tabla' }" @click="vista = 'tabla'">Tabla</button>
            <button class="vtab" :class="{ activo: vista === 'grafico' }" @click="vista = 'grafico'">Gráfico</button>
          </div>
          <button
            v-if="vista === 'tabla'"
            class="btn btn-sm"
            :class="modoEmparejar ? 'emparejar-on' : 'emparejar-off'"
            @click="toggleEmparejar"
          >
            {{ modoEmparejar ? '✓ Emparejando' : 'Emparejar manual' }}
          </button>
        </div>
      </div>

      <p v-if="modoEmparejar && vista === 'tabla'" class="ayuda-emparejar">
        Elige, por cada componente, con cuál del periodo anterior se compara (útil cuando cambió el nombre).
      </p>

      <!-- ═══ TABLA ═══ -->
      <div v-show="vista === 'tabla'" class="contenedor-tabla">
        <table class="lista-tabla comparativo">
          <thead>
            <tr>
              <th>{{ factorSel ? 'Característica' : 'Factor' }}</th>
              <th v-if="modoEmparejar">Emparejar con (anterior)</th>
              <th>Grado de cumplimiento</th>
              <th class="col-num">Calif. {{ comparacion.actual.periodo }}</th>
              <th class="col-num">%</th>
              <th class="col-num">Calif. {{ comparacion.anterior.periodo }}</th>
              <th class="col-num">%</th>
              <th class="col-num">Variación</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="f in filasOrdenadas"
              :key="f.id_componente"
              :class="{ clickable: !factorSel && !modoEmparejar }"
              @click="(!factorSel && !modoEmparejar) && drill(f.id_componente)"
            >
              <td class="col-nombre">
                <span v-if="f.ordinal" class="ordinal">{{ fmtOrdinal(f.ordinal) }}</span>
                {{ f.nombre }}
                <span v-if="!factorSel && !modoEmparejar" class="drill-hint">ver características ›</span>
              </td>
              <td v-if="modoEmparejar" class="col-pareo" @click.stop>
                <select
                  class="input-select select-pareo"
                  :disabled="guardandoPareo === f.id_componente"
                  :value="f.anterior_componente?.id_componente ?? ''"
                  @change="onCambiarPareo(f, $event)"
                >
                  <option value="">— Automático —</option>
                  <option v-for="c in componentesAnteriores" :key="c.id_componente" :value="c.id_componente">
                    {{ (c.ordinal ? fmtOrdinal(c.ordinal) + ' · ' : '') + c.nombre }}
                  </option>
                </select>
              </td>
              <td>
                <span v-if="f.grado" class="badge-grado" :class="nivelClase(f.actual.calif)">{{ f.grado }}</span>
                <span v-else class="vacio">—</span>
              </td>
              <td class="col-num">
                <div v-if="f.actual.calif !== null" class="databar-wrap">
                  <div class="databar" :class="nivelClase(f.actual.calif)" :style="{ width: barWidth(f.actual.pct) }"></div>
                  <span class="databar-val">{{ fmtCalif(notaEn(f.actual)) }}</span>
                </div>
                <span v-else class="vacio">—</span>
              </td>
              <td class="col-num">{{ fmtPct(f.actual.pct) }}</td>
              <td class="col-num">
                <template v-if="f.anterior">
                  <div v-if="f.anterior.calif !== null" class="databar-wrap">
                    <div class="databar databar-tenue" :style="{ width: barWidth(f.anterior.pct) }"></div>
                    <span class="databar-val">{{ fmtCalif(notaEn(f.anterior)) }}</span>
                  </div>
                  <span v-else class="vacio">—</span>
                </template>
                <span v-else class="sin-equiv" title="Sin equivalente en el periodo anterior (empareja manualmente)">sin equiv.</span>
              </td>
              <td class="col-num">{{ f.anterior ? fmtPct(f.anterior.pct) : '—' }}</td>
              <td class="col-num">
                <span v-if="variacionDe(f) !== null" class="variacion" :class="varClase(variacionDe(f))">
                  {{ varFlecha(variacionDe(f)) }}
                  {{ textoVariacion(variacionDe(f)) }}
                </span>
                <span v-else class="vacio">—</span>
              </td>
            </tr>

            <tr v-if="!comparacion.filas.length">
              <td :colspan="modoEmparejar ? 8 : 7" class="mensaje-vacio">No hay datos consolidados para comparar en este nivel.</td>
            </tr>
          </tbody>

          <tfoot v-if="comparacion.global_factor">
            <tr class="fila-global">
              <td class="col-nombre">CALIFICACIÓN GLOBAL {{ factorSel ? 'DEL FACTOR' : 'DEL MODELO' }}</td>
              <td v-if="modoEmparejar"></td>
              <td>
                <span v-if="comparacion.global_factor.grado" class="badge-grado" :class="nivelClase(comparacion.global_factor.actual.calif)">
                  {{ comparacion.global_factor.grado }}
                </span>
                <span v-else class="vacio">—</span>
              </td>
              <td class="col-num">{{ fmtCalif(notaEn(comparacion.global_factor.actual)) }}</td>
              <td class="col-num">{{ fmtPct(comparacion.global_factor.actual.pct) }}</td>
              <td class="col-num">{{ comparacion.global_factor.anterior ? fmtCalif(notaEn(comparacion.global_factor.anterior)) : '—' }}</td>
              <td class="col-num">{{ comparacion.global_factor.anterior ? fmtPct(comparacion.global_factor.anterior.pct) : '—' }}</td>
              <td class="col-num">
                <span v-if="variacionDe(comparacion.global_factor) !== null" class="variacion" :class="varClase(variacionDe(comparacion.global_factor))">
                  {{ varFlecha(variacionDe(comparacion.global_factor)) }}
                  {{ textoVariacion(variacionDe(comparacion.global_factor)) }}
                </span>
                <span v-else class="vacio">—</span>
              </td>
            </tr>
          </tfoot>
        </table>
        <p class="fuente">La variación se expresa en puntos porcentuales (% actual − % anterior).</p>
      </div>

      <!-- ═══ GRÁFICO (barras por año: actual vs anterior) ═══ -->
      <div v-show="vista === 'grafico'" class="grafico-comp">
        <div class="leyenda">
          <span class="leyenda-item"><span class="cuadro cuadro-actual"></span> {{ comparacion.actual.periodo }} (actual)</span>
          <span class="leyenda-item"><span class="cuadro cuadro-anterior"></span> {{ comparacion.anterior.periodo }} (anterior)</span>
          <span class="grafico-nivel">{{ factorSel ? 'Por característica' : 'Por factor' }}</span>
        </div>

        <div v-if="!barras.length" class="mensaje-vacio">No hay datos para graficar.</div>
        <div v-else class="contenedor-tabla">
          <svg :viewBox="`0 0 ${anchoSvg} 240`" class="grafico-svg" preserveAspectRatio="xMidYMid meet">
            <!-- eje Y -->
            <line x1="42" y1="12" x2="42" y2="190" stroke="#ccc" />
            <line x1="42" y1="190" :x2="anchoSvg - 8" y2="190" stroke="#ccc" />
            <template v-for="t in ejeTicks" :key="'t' + t">
              <line x1="38" :y1="yPos(t)" x2="42" :y2="yPos(t)" stroke="#bbb" />
              <line x1="42" :y1="yPos(t)" :x2="anchoSvg - 8" :y2="yPos(t)" stroke="#f0f0f0" />
              <text x="34" :y="yPos(t) + 3" text-anchor="end" class="eje-label">{{ t }}</text>
            </template>
            <!-- grupos -->
            <g v-for="(b, i) in barras" :key="b.id">
              <rect
                :x="grupoX(i)" :y="yPos(b.actual ?? 0)" :width="anchoBarra" :height="190 - yPos(b.actual ?? 0)"
                fill="#0e69a0" rx="2"
              />
              <text v-if="b.actual !== null" :x="grupoX(i) + anchoBarra / 2" :y="yPos(b.actual) - 3" text-anchor="middle" class="barra-val">{{ b.actual.toFixed(2) }}</text>
              <rect
                :x="grupoX(i) + anchoBarra + 4" :y="yPos(b.anterior ?? 0)" :width="anchoBarra" :height="190 - yPos(b.anterior ?? 0)"
                fill="#eea222" rx="2"
              />
              <text v-if="b.anterior !== null" :x="grupoX(i) + anchoBarra * 1.5 + 4" :y="yPos(b.anterior) - 3" text-anchor="middle" class="barra-val">{{ b.anterior.toFixed(2) }}</text>
              <text :x="grupoX(i) + anchoBarra + 2" y="204" text-anchor="middle" class="barra-label">{{ b.ordinal ? fmtOrdinal(b.ordinal) : b.nombre.slice(0, 10) }}</text>
            </g>
          </svg>
        </div>
        <p class="fuente">Barras: calificación (0–{{ comparacion.escala_max }}) por periodo. Azul = actual, dorado = anterior.</p>
      </div>
    </template>

    <MensajeFlotante :visible="msgVisible" :mensaje="msgTexto" :tipo="msgTipo" @cerrar="msgVisible = false" />
  </div>
</template>

<script setup lang="ts">
/**
 * Comparativo entre periodos.
 *
 * Requiere un esquema de correspondencias declarado a mano: los componentes sin
 * equivalencia no aparecen en el comparativo.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/ejecucion/:id_ejecucion/comparacion` (ComparacionCalificaciones) — permiso `calificacion.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useGestionComparacion } from '@/store/GestionComparacion'
import { useGestionProcesos } from '@/store/GestionProcesoAutoevaluacion'
import { getProcesoByIdService } from '@/services/ProcesoAutoevaluacionService'
import {
  emparejarComponentesService,
  desemparejarComponenteService,
  getComponentesVersionService,
  type ComponentePlano
} from '@/services/ComparacionService'
import type { Proceso } from '@/interfaces/ProcesoAutoevaluacion'
import type { FilaComparacion } from '@/interfaces/ComparacionCalificacion'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const route = useRoute()
const idEjecucion = Number(route.params.id_ejecucion)

const comparStore = useGestionComparacion()
const procesosStore = useGestionProcesos()

const procesoActual = ref<Proceso | null>(null)
const anteriorSel = ref<number | null>(null)
const factorSel = ref<number | null>(null)
const factores = ref<{ id: number; nombre: string; ordinal: string | null }[]>([])
const listo = ref(false)

const vista = ref<'tabla' | 'grafico'>('tabla')
const modoEmparejar = ref(false)
const componentesAnteriores = ref<ComponentePlano[]>([])
const guardandoPareo = ref<number | null>(null)

const msgVisible = ref(false)
const msgTexto = ref('')
const msgTipo = ref<'positivo' | 'error'>('positivo')

const comparacion = computed(() => comparStore.comparacion)

const candidatosAnterior = computed(() => {
  if (!procesoActual.value) return []
  const base = procesoActual.value
  return procesosStore.procesos
    .filter(p =>
      p.id !== idEjecucion &&
      p.ambito === base.ambito &&
      (p.id_programa ?? null) === (base.id_programa ?? null)
    )
    .sort((a, b) => (b.periodo ?? '').localeCompare(a.periodo ?? ''))
})

function toast(texto: string, tipo: 'positivo' | 'error' = 'positivo') {
  msgTexto.value = texto
  msgTipo.value = tipo
  msgVisible.value = true
}

async function cargar(): Promise<void> {
  if (!anteriorSel.value) {
    comparStore.limpiar()
    return
  }
  await comparStore.cargar(idEjecucion, {
    anterior: anteriorSel.value,
    componente: factorSel.value ?? undefined
  })
  if (!factorSel.value && comparStore.comparacion) {
    factores.value = comparStore.comparacion.filas
      .map(f => ({ id: f.id_componente, nombre: f.nombre, ordinal: f.ordinal }))
      .sort((a, b) => cmpOrdinal(a.ordinal, b.ordinal))
  }
  // Componentes del periodo anterior (para el emparejamiento manual).
  const idVersionAnterior = comparStore.comparacion?.anterior?.id_version
  componentesAnteriores.value = idVersionAnterior
    ? (await getComponentesVersionService(idVersionAnterior))
        .filter(c => c.nivel > 1)
        .sort((a, b) => cmpOrdinal(a.ordinal ?? null, b.ordinal ?? null))
    : []
}

function drill(idComponente: number): void {
  factorSel.value = idComponente
}

function toggleEmparejar(): void {
  modoEmparejar.value = !modoEmparejar.value
  if (modoEmparejar.value && !componentesAnteriores.value.length) {
    toast('No se pudo cargar la estructura del periodo anterior.', 'error')
  }
}

async function onCambiarPareo(fila: FilaComparacion, ev: Event): Promise<void> {
  const val = (ev.target as HTMLSelectElement).value
  guardandoPareo.value = fila.id_componente
  const res = val
    ? await emparejarComponentesService(fila.id_componente, Number(val))
    : await desemparejarComponenteService(fila.id_componente)
  guardandoPareo.value = null

  if (res.ok) {
    toast(val ? 'Emparejamiento guardado.' : 'Emparejamiento automático restaurado.')
    await cargar()
  } else {
    toast(res.error ?? 'No se pudo guardar.', 'error')
  }
}

function fmtOrdinal(ordinal: string | null | undefined): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

// ── Orden numérico de ordinales ("1.10" va después de "1.2") ─────────────────

function cmpOrdinal(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

// ── Formato / colores ────────────────────────────────────────────────────────

function fmtCalif(v: number | null): string { return v === null ? '—' : v.toFixed(2) }

/**
 * Escala en la que se leen las notas de la tabla.
 *
 * `null` = cada periodo con la suya (como estaba). Al elegir 5 o 10 se muestran
 * las equivalencias que ya calcula el backend: no se convierte aquí, porque una
 * segunda implementación de la fórmula acabaría discrepando de la primera.
 */
const escalaUnificada = ref<5 | 10 | null>(null)

/** Nota de una celda en la escala elegida. */
function notaEn(celda: { calif: number | null; en_cinco?: number | null; en_diez?: number | null } | null): number | null {
  if (!celda) return null
  if (escalaUnificada.value === 5) return celda.en_cinco ?? null
  if (escalaUnificada.value === 10) return celda.en_diez ?? null
  return celda.calif
}

/**
 * Variación a mostrar.
 *
 * Con escala unificada se usa la variación en PUNTOS que trae el backend
 * (calculada con los dos periodos en 0–5); sin unificar, el porcentaje, que es
 * lo único comparable cuando cada periodo va en su escala.
 */
function variacionDe(fila: { variacion: number | null; variacion_nota?: number | null }): number | null {
  return escalaUnificada.value === null ? fila.variacion : (fila.variacion_nota ?? null)
}

/** Texto de la variación: puntos porcentuales sin unificar, puntos de nota con escala unificada. */
function textoVariacion(v: number | null): string {
  if (v === null) return '—'
  const signo = v > 0 ? '+' : ''
  return escalaUnificada.value === null
    ? `${signo}${v.toFixed(1)}`
    : `${signo}${v.toFixed(2)} pts`
}
function fmtPct(v: number | null): string { return v === null ? '—' : `${v.toFixed(1)}%` }
function barWidth(pct: number | null): string { return `${Math.max(0, Math.min(100, pct ?? 0))}%` }

function nivelClase(calif: number | null): string {
  if (calif === null || !comparacion.value) return ''
  const escala = comparacion.value.escala_max || 5
  const norm = escala > 0 ? (calif / escala) * 5 : calif
  if (norm >= 4.5) return 'nivel-excelente'
  if (norm >= 4.0) return 'nivel-alto'
  if (norm >= 3.0) return 'nivel-medio'
  return 'nivel-bajo'
}

function varClase(v: number): string { return v > 0 ? 'var-pos' : v < 0 ? 'var-neg' : 'var-neutra' }
function varFlecha(v: number): string { return v > 0 ? '▲' : v < 0 ? '▼' : '—' }

// ── Gráfico de barras (actual vs anterior) ───────────────────────────────────

const filasOrdenadas = computed(() =>
  [...(comparacion.value?.filas ?? [])].sort((a, b) => cmpOrdinal(a.ordinal, b.ordinal))
)

const barras = computed(() =>
  filasOrdenadas.value.map(f => ({
    id: f.id_componente,
    ordinal: f.ordinal,
    nombre: f.nombre,
    actual: f.actual.calif,
    anterior: f.anterior?.calif ?? null
  }))
)

const anchoBarra = 20
const pasoGrupo = 62
const anchoSvg = computed(() => Math.max(300, 60 + barras.value.length * pasoGrupo))
function grupoX(i: number): number { return 56 + i * pasoGrupo }

const escalaMax = computed(() => comparacion.value?.escala_max || 5)
const ejeTicks = computed(() => {
  const max = Math.ceil(escalaMax.value)
  const paso = max <= 5 ? 1 : 2
  const ticks: number[] = []
  for (let t = 0; t <= max; t += paso) ticks.push(t)
  return ticks
})
function yPos(v: number): number {
  const max = escalaMax.value || 5
  return 190 - (Math.max(0, Math.min(max, v)) / max) * 178
}

// ── Carga inicial ────────────────────────────────────────────────────────────

watch([anteriorSel, factorSel], () => { if (listo.value) cargar() })

onMounted(async () => {
  const [proceso] = await Promise.all([
    getProcesoByIdService(idEjecucion),
    procesosStore.cargarTodos()
  ])
  procesoActual.value = proceso

  const enlazada = proceso?.id_ejecucion_anterior ?? proceso?.anterior?.id_ejecucion ?? null
  anteriorSel.value = enlazada ?? candidatosAnterior.value[0]?.id ?? null

  listo.value = true
  await cargar()
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white; padding: 2rem; border-radius: var(--radius-lg);
  flex: 1; min-height: 0; overflow-y: auto;
  box-shadow: var(--shadow-md); box-sizing: border-box; margin: 20px; font-family: var(--font-family);
}
.header-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
h1 { font-size: 1.4rem; font-weight: 700; margin: 0; color: var(--text-primary); }
.subtitulo { font-size: 0.85rem; color: #666; margin: 0.25rem 0 0; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }

.controles { display: flex; flex-wrap: wrap; gap: 1.25rem; align-items: flex-end; margin-bottom: 1.25rem; }
.control { display: flex; flex-direction: column; gap: 0.35rem; }
.control > label { font-size: 0.8rem; font-weight: 600; color: #555; }
.control-btn { justify-content: flex-end; }
.input-select {
  padding: 0.5rem 0.75rem; border: 1px solid #e6e5e5; border-radius: var(--radius-md);
  font-size: 0.9rem; font-family: var(--font-family); background-color: #f9f9f9; min-width: 220px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.input-select:focus { outline: none; border-color: #0e69a0; box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1); }
.input-select:disabled { opacity: 0.6; }
.select-pareo { min-width: 220px; }

.aviso { padding: 0.85rem 1rem; background: #fff3cd; color: #856404; border-radius: var(--radius-md); font-size: 0.88rem; }
.aviso-error { background: #f8d7da; color: #721c24; }
.info { padding: 1rem 0; color: #555; }

/* Barra superior */
.barra-superior { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
.periodos-bar { display: flex; gap: 0.5rem; }
.badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-weight: 600; font-size: 0.8rem; }
.badge-actual { background-color: #e8f4fd; color: #0e69a0; }
.badge-anterior { background-color: #fdf1dc; color: #8a6100; }
.acciones-derecha { display: flex; align-items: center; gap: 0.75rem; }
.vista-tabs { display: inline-flex; border: 1px solid #cfe0ee; border-radius: 8px; overflow: hidden; }
.vtab { padding: 0.4rem 0.9rem; border: none; background: #fff; color: #555; font-weight: 600; font-size: 0.82rem; cursor: pointer; font-family: var(--font-family); }
.vtab.activo { background: #0e69a0; color: #fff; }
.ayuda-emparejar { font-size: 0.82rem; color: #856404; background: #fff8e6; border: 1px solid #f0d98a; border-radius: 8px; padding: 0.5rem 0.85rem; margin: 0 0 0.75rem; }

/* Tabla */
.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; }
.lista-tabla th, .lista-tabla td { padding: 10px 14px; text-align: left; border-bottom: 1px solid #e0e0e0; font-size: 0.9rem; }
.lista-tabla thead { background-color: #f5f5f5; font-weight: 600; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.05em; color: #555; }
.col-num { text-align: right; white-space: nowrap; }
.col-nombre { font-weight: 500; }
.col-pareo { min-width: 240px; }
.ordinal { display: inline-block; min-width: 2.2em; color: #0e69a0; font-weight: 700; }
.vacio { color: #bbb; }
.clickable { cursor: pointer; transition: background-color 0.12s ease; }
.clickable:hover { background-color: #f4faff; }
.drill-hint { margin-left: 0.5rem; font-size: 0.72rem; color: #0e69a0; opacity: 0; transition: opacity 0.12s ease; }
.clickable:hover .drill-hint { opacity: 1; }
.badge-grado { display: inline-block; padding: 2px 9px; border-radius: 10px; background: #f0f0f0; font-size: 0.76rem; font-weight: 600; white-space: nowrap; }

.databar-wrap { position: relative; display: flex; align-items: center; justify-content: flex-end; min-width: 90px; height: 22px; background: #f4f4f4; border-radius: 4px; overflow: hidden; }
.databar { position: absolute; left: 0; top: 0; bottom: 0; opacity: 0.28; }
.databar-tenue { background: #9aa0a6; opacity: 0.22; }
.databar-val { position: relative; z-index: 1; padding: 0 8px; font-weight: 600; font-size: 0.85rem; }
.sin-equiv { font-size: 0.75rem; color: #b08900; font-style: italic; }

.variacion { font-weight: 700; font-size: 0.85rem; white-space: nowrap; }
.var-pos { color: #28a745; }
.var-neg { color: #dc3545; }
.var-neutra { color: #999; }

.nivel-excelente { color: #28a745; }
.nivel-alto { color: #17a2b8; }
.nivel-medio { color: #d39e00; }
.nivel-bajo { color: #dc3545; }
.databar.nivel-excelente { background: #28a745; }
.databar.nivel-alto { background: #17a2b8; }
.databar.nivel-medio { background: #ffc107; }
.databar.nivel-bajo { background: #dc3545; }
.badge-grado.nivel-excelente { background: #d4edda; color: #155724; }
.badge-grado.nivel-alto { background: #d1ecf1; color: #0c5460; }
.badge-grado.nivel-medio { background: #fff3cd; color: #856404; }
.badge-grado.nivel-bajo { background: #f8d7da; color: #721c24; }

.fila-global td { background-color: #eef5fb; border-top: 2px solid #0e69a0; font-weight: 700; }
.fila-global .col-nombre { color: #0e69a0; text-transform: uppercase; font-size: 0.82rem; letter-spacing: 0.03em; }

.mensaje-vacio { color: #888; font-style: italic; text-align: center; padding: 1.25rem 0; }
.fuente { font-size: 0.75rem; color: #999; margin: 0.75rem 0 0; font-style: italic; }

/* Gráfico */
.grafico-comp { margin-top: 0.5rem; }
.leyenda { display: flex; align-items: center; gap: 1.25rem; margin-bottom: 0.5rem; font-size: 0.82rem; color: #555; }
.leyenda-item { display: inline-flex; align-items: center; gap: 0.4rem; }
.cuadro { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
.cuadro-actual { background: #0e69a0; }
.cuadro-anterior { background: #eea222; }
.grafico-nivel { margin-left: auto; font-weight: 600; color: #0e69a0; }
.grafico-svg { width: 100%; height: auto; min-width: 300px; }
.eje-label { font-size: 9px; fill: #888; }
.barra-val { font-size: 9px; font-weight: 700; fill: #333; }
.barra-label { font-size: 9px; fill: #555; }

/* Botones */
.btn { padding: 0.6rem 1.25rem; font-weight: 600; border: none; border-radius: 8px; cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.9rem; }
.btn-sm { padding: 0.4rem 0.85rem; font-size: 0.8rem; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.emparejar-off { background: #fff; border: 1px solid #0e69a0; color: #0e69a0; }
.emparejar-off:hover { background: #0e69a0; color: #fff; }
.emparejar-on { background: #0e69a0; color: #fff; }

@media (max-width: 640px) {
  .formulario-modelo { padding: 1rem; }
  .input-select { min-width: 160px; }
}
</style>
